/**
 * smoothEmitterConfig — config + pure lifecycle math for the
 * SmoothParticleEmitter.
 *
 * The brief's "smooth particles and movement": particles whose scale, alpha,
 * travel and colour all ride EASED curves (never linear ramps), spawned on a
 * steady cadence, soft + additive. This module owns the math:
 *   - spawnCountForElapsed: cadence (accumulator-style, no drift)
 *   - particleAt:           the eased per-particle state at life-fraction t
 * The emitter class (smoothParticleEmitter.ts) only pools sprites + applies
 * these values. Keeping the curves here makes them unit-testable + tunable.
 */
import { EASING, type VfxEasingName } from './easing';
import type { VfxBlendMode } from '../../rendering/IRenderer';
import { lerp } from '../../../shared/utils/MathUtils';

export interface SmoothEmitterConfig {
  /** Base particle colour (tints the soft glow sprite). */
  color?: number;
  /** End-of-life colour — particle lerps colour→colourEnd over its life
   *  (the fire→smoke / spark-cools beat). Omit for a constant colour. */
  colorEnd?: number;
  /** Ms between spawns (cadence). */
  frequencyMs?: number;
  /** Particle lifetime (ms). */
  lifespanMs?: number;
  /** Travel speed along the emit angle (px/sec). */
  speed?: number;
  /** Constant downward accel (px/sec²); negative = upward (embers). */
  gravityY?: number;
  /** Steady horizontal drift VELOCITY (px/sec) layered onto the bearing
   *  travel — the weather system's `windX`. Additive; default 0 (no drift). */
  windX?: number;
  /** Constant horizontal accel (px/sec²); the lateral twin of gravityY (e.g.
   *  a building gust). Additive; default 0. */
  gravityX?: number;
  /** Scale envelope (start → end over life). */
  scaleStart?: number;
  scaleEnd?: number;
  /** Alpha envelope — eases UP to `alphaEnd` (the peak), then fades to 0 by
   *  death, so every particle has a soft in/out (no hard pop). */
  alphaStart?: number;
  alphaEnd?: number;
  /** Easing curve applied to all per-particle property motion. */
  ease?: VfxEasingName;
  blendMode?: VfxBlendMode;
}

export interface SmoothEmitterSpec {
  color: number;
  colorEnd?: number;
  frequencyMs: number;
  lifespanMs: number;
  speed: number;
  gravityY: number;
  windX: number;
  gravityX: number;
  scaleStart: number;
  scaleEnd: number;
  alphaStart: number;
  alphaEnd: number;
  ease: VfxEasingName;
  blendMode: VfxBlendMode;
}

/** Per-particle invariants chosen at spawn (position + emit bearing). */
export interface ParticleSeed {
  x: number;
  y: number;
  angleRad: number;
}

/** Resolved per-particle render state at a life-fraction. */
export interface ParticleState {
  x: number;
  y: number;
  scale: number;
  alpha: number;
  color: number;
}

export const DEFAULT_SMOOTH_EMITTER: SmoothEmitterSpec = {
  color: 0xfff2c8,
  frequencyMs: 120,
  lifespanMs: 1600,
  speed: 24,
  gravityY: 0,
  windX: 0,
  gravityX: 0,
  scaleStart: 0.3,
  scaleEnd: 1.0,
  alphaStart: 0,
  alphaEnd: 0.55,
  ease: 'cubicOut',
  blendMode: 'add',
};

export function resolveSmoothEmitter(cfg: SmoothEmitterConfig): SmoothEmitterSpec {
  const d = DEFAULT_SMOOTH_EMITTER;
  return {
    color: cfg.color ?? d.color,
    colorEnd: cfg.colorEnd,
    frequencyMs: cfg.frequencyMs ?? d.frequencyMs,
    lifespanMs: cfg.lifespanMs ?? d.lifespanMs,
    speed: cfg.speed ?? d.speed,
    gravityY: cfg.gravityY ?? d.gravityY,
    windX: cfg.windX ?? d.windX,
    gravityX: cfg.gravityX ?? d.gravityX,
    scaleStart: cfg.scaleStart ?? d.scaleStart,
    scaleEnd: cfg.scaleEnd ?? d.scaleEnd,
    alphaStart: cfg.alphaStart ?? d.alphaStart,
    alphaEnd: cfg.alphaEnd ?? d.alphaEnd,
    ease: cfg.ease ?? d.ease,
    blendMode: cfg.blendMode ?? d.blendMode,
  };
}

/**
 * How many particles to spawn between two accumulator readings. The caller
 * tracks total elapsed ms; this returns floor(now/freq) − floor(prev/freq),
 * so cadence never drifts and frame-rate doesn't change the spawn rate.
 */
export function spawnCountForElapsed(prevElapsedMs: number, nowElapsedMs: number, frequencyMs: number): number {
  if (frequencyMs <= 0) return 0;
  const already = Math.floor(prevElapsedMs / frequencyMs);
  const total = Math.floor(nowElapsedMs / frequencyMs);
  return Math.max(0, total - already);
}

/** Lerp two 0xRRGGBB colours channel-wise at t. */
function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(lerp(ar, br, t));
  const g = Math.round(lerp(ag, bg, t));
  const bl = Math.round(lerp(ab, bb, t));
  return (r << 16) | (g << 8) | bl;
}

/**
 * Particle render state at life-fraction `t` in [0,1]. Position eases along
 * the emit bearing (plus gravity), scale + colour ease start→end, and alpha
 * follows a soft in/out envelope (ramps to the peak `alphaEnd`, fades to 0).
 * All non-linear — the "smooth movement" the look needs.
 */
export function particleAt(spec: SmoothEmitterSpec, seed: ParticleSeed, t: number): ParticleState {
  const tt = t < 0 ? 0 : t > 1 ? 1 : t;
  const e = EASING[spec.ease](tt);
  const lifeSec = spec.lifespanMs / 1000;

  // Eased travel distance along the bearing, with gravity as a quadratic pull.
  const dist = spec.speed * lifeSec * e;
  const elapsedSec = lifeSec * tt;
  const gravFall = 0.5 * spec.gravityY * elapsedSec * elapsedSec;
  // Wind = a steady horizontal VELOCITY (linear in elapsed time, NOT eased —
  // a constant drift), gravityX = its quadratic-accel twin. Both additive.
  const windDrift = spec.windX * elapsedSec;
  const gravDriftX = 0.5 * spec.gravityX * elapsedSec * elapsedSec;

  // Alpha: ramp up to the peak over the first 30% of life, then ease out.
  const RAMP = 0.3;
  let alpha: number;
  if (tt <= RAMP) {
    alpha = lerp(spec.alphaStart, spec.alphaEnd, EASING.cubicOut(tt / RAMP));
  } else {
    alpha = lerp(spec.alphaEnd, 0, EASING.cubicOut((tt - RAMP) / (1 - RAMP)));
  }

  return {
    x: seed.x + Math.cos(seed.angleRad) * dist + windDrift + gravDriftX,
    y: seed.y + Math.sin(seed.angleRad) * dist + gravFall,
    scale: lerp(spec.scaleStart, spec.scaleEnd, e),
    alpha,
    color: spec.colorEnd === undefined ? spec.color : lerpColor(spec.color, spec.colorEnd, e),
  };
}
